import { LogOutIcon, type LucideIcon } from "lucide-react";
import { useCallback, useState, type ComponentProps } from "react";
import { useNavigate } from "react-router";

import { useMe } from "@/api/hooks/auth";
import { useCourses } from "@/api/hooks/courses";
import type { CourseSummary } from "@/api/types";
import { CourseChip } from "@/components/common/course-chip";
import { oppositeTheme, themeOption } from "@/components/theme/theme-options";
import { useAccountTheme } from "@/components/theme/use-account-theme";
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";

import { COURSE_SECTIONS, courseAction, coursePath, courseSectionPath, PRIMARY_NAV, SETTINGS_NAV } from "./nav-config";
import { usePaletteShortcut } from "./palette-shortcut";
import { useSignOut } from "./use-sign-out";

interface CommandPaletteProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/** ⌘K / Ctrl+K palette: jump to pages and courses, run account actions. */
export function CommandPalette({ open, onOpenChange }: CommandPaletteProps) {
  const toggle = useCallback(() => onOpenChange(!open), [open, onOpenChange]);
  usePaletteShortcut(toggle);

  return (
    <CommandDialog open={open} onOpenChange={onOpenChange}>
      {/* Mounted only while open, so the search resets every time the palette opens. */}
      <PaletteCommands onDone={() => onOpenChange(false)} />
    </CommandDialog>
  );
}

function PaletteCommands({ onDone }: { onDone: () => void }) {
  const [search, setSearch] = useState("");
  const navigate = useNavigate();
  const me = useMe();
  const courses = useCourses().data ?? [];
  const { resolvedTheme, toggleTheme } = useAccountTheme();
  const signOut = useSignOut();

  const run = (action: () => unknown) => () => {
    onDone();
    action();
  };
  const goTo = (to: string) => run(() => navigate(to));

  const addCourse = me ? courseAction(me.role) : null;
  const nextTheme = themeOption(oppositeTheme(resolvedTheme));
  const isInstructor = me?.role === "instructor";

  return (
    <>
      <CommandInput value={search} onValueChange={setSearch} placeholder="Search pages, courses and actions…" />
      <CommandList>
        <CommandEmpty>No results found.</CommandEmpty>

        <CommandGroup heading="Navigate">
          {[...PRIMARY_NAV, SETTINGS_NAV].map((item) => (
            <PaletteItem key={item.to} icon={item.icon} onSelect={goTo(item.to)}>
              {item.label}
            </PaletteItem>
          ))}
        </CommandGroup>

        {courses.length > 0 && (
          <CommandGroup heading="Courses">
            {courses.map((course) => (
              <CommandItem
                key={course.id}
                value={courseValue(course)}
                keywords={isInstructor ? ["overview"] : undefined}
                onSelect={goTo(coursePath(course.id))}
              >
                <CourseChip course={course} className="shrink-0 text-foreground" />
                <span className="truncate text-muted-foreground">{course.name}</span>
              </CommandItem>
            ))}
            {/* Per-course pages would crowd the default list, so they appear once the user types. */}
            {isInstructor &&
              search.trim() !== "" &&
              courses.map((course) => <CourseSectionItems key={course.id} course={course} goTo={goTo} />)}
          </CommandGroup>
        )}

        <CommandGroup heading="Actions">
          {addCourse && (
            <PaletteItem icon={addCourse.icon} onSelect={goTo(addCourse.to)}>
              {addCourse.label}
            </PaletteItem>
          )}
          <PaletteItem
            icon={nextTheme.icon}
            keywords={["theme", "appearance", "dark mode", "light mode"]}
            onSelect={run(toggleTheme)}
          >
            Switch to {nextTheme.label.toLowerCase()} theme
          </PaletteItem>
          <PaletteItem icon={LogOutIcon} keywords={["log out", "logout"]} onSelect={run(signOut)}>
            Sign out
          </PaletteItem>
        </CommandGroup>
      </CommandList>
    </>
  );
}

interface CourseSectionItemsProps {
  course: CourseSummary;
  goTo: (to: string) => () => void;
}

function CourseSectionItems({ course, goTo }: CourseSectionItemsProps) {
  return COURSE_SECTIONS.filter((section) => section.segment).map((section) => (
    <PaletteItem
      key={section.segment}
      icon={section.icon}
      value={`${section.label} ${courseValue(course)}`}
      onSelect={goTo(courseSectionPath(course.id, section))}
    >
      {section.label}
      <CourseChip course={course} className="ml-auto" />
    </PaletteItem>
  ));
}

/** cmdk matches on this text; the id keeps same-named courses (e.g. two terms) distinct. */
function courseValue(course: CourseSummary): string {
  return `${course.code} ${course.name} ${course.term} #${course.id}`;
}

function PaletteItem({ icon: Icon, children, ...props }: ComponentProps<typeof CommandItem> & { icon: LucideIcon }) {
  return (
    <CommandItem {...props}>
      <Icon aria-hidden />
      {children}
    </CommandItem>
  );
}
